import React, { useState } from 'react';
import { Alert, Stack, TextField } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { createDirectory, patchDirectory } from '../../actions/supervisorActions';
import { useNavigateIfNeeded } from '../../util/useNavigateIfNeeded';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ParentSelect } from '../interface/ParentSelect';
import { EditDirectoryDialogProps } from './dialogs';

export function EditDirectoryDialog(props: EditDirectoryDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigateIfNeeded();

  const linesState = useAppSelector((state) => state.lines);

  const [didFail, setDidFail] = useState(false);

  let name: string | undefined;
  const [editedName, setEditedName] = useState<string>();
  const [nameProblem, setNameProblem] = useState<string>();

  let parentGuid: string | undefined;
  const [editedParentGuid, setEditedParentGuid] = useState<string | null | undefined>();
  function applyEditedParentGuid(currentParentGuid?: string): string | undefined {
    // null means the existing parent guid has been removed by user selection.
    if (editedParentGuid === null) return undefined;
    return editedParentGuid ?? currentParentGuid;
  }

  if (props.mode === 'create') {
    name = editedName;
    parentGuid = applyEditedParentGuid(props.parentGuid);
  } else {
    const directory = linesState.directories[props.directoryGuid];
    if (linesState.status !== 'loaded' || directory == null) return null;

    name = editedName ?? directory.name;
    parentGuid = applyEditedParentGuid(directory.parentGuid);
  }

  async function onSubmit() {
    if (props.mode === 'edit' && !editedName && !editedParentGuid) {
      dispatch(closeCurrentPopup());
      return;
    }

    if (name == null || name.trim() === '') {
      setNameProblem('name is required');
      return;
    }

    setDidFail(false);
    setNameProblem(undefined);

    try {
      switch (props.mode) {
        case 'create':
          await dispatch(createDirectory(name, parentGuid));
          break;

        case 'edit':
          await dispatch(patchDirectory(props.directoryGuid, { name: editedName, parentGuid: editedParentGuid }));
          break;
      }
    } catch (error) {
      setDidFail(true);

      console.error(error);

      return;
    }

    dispatch(closeCurrentPopup());

    if (props.mode === 'create' && parentGuid) {
      navigate(`/directories/${parentGuid}`);
    }
  }

  const actionString = props.mode === 'create' ? 'Create' : 'Edit';

  return (
    <Dialog
      title={`${actionString} directory`}
      submitText={actionString}
      onSubmit={onSubmit}
      onCancel="close"
      enterKeySubmits
    >
      <Stack spacing={2} sx={{ marginTop: 1 }}>
        {didFail && (
          <Alert variant="outlined" severity="error">
            Unable to {actionString.toLowerCase()} directory.
          </Alert>
        )}

        <TextField
          size="small"
          fullWidth
          autoFocus
          autoComplete="off"
          label="Directory Name"
          sx={{ marginTop: 1 }}
          value={name}
          error={!!nameProblem}
          onChange={(event) => setEditedName(event.target.value)}
          onFocus={(event) => event.target.select()}
        />

        <ParentSelect
          includeDirectories
          label="Parent"
          optional={props.mode === 'edit' || !parentGuid}
          parentGuid={parentGuid}
          onChange={setEditedParentGuid}
        />
      </Stack>
    </Dialog>
  );
}
